import styled from "styled-components";
import { SbInput, SbSelect } from "uiCore";


// box-sizing: border-box makes \`width\` the true, guaranteed outer footprint
// regardless of border/padding — this box's left border is 4px vs 1px on
// every other side, and under the default content-box sizing that asymmetry
// pushed the real rendered width to an odd number of pixels (200 content +
// 20 padding + 5 border = 225px), which is exactly what makes the tree's
// 50%-split connector math drift by a sub-pixel at ancestor levels. Fixing
// the box's sizing model, not the connector CSS, is the actual fix.
//
// Sizes come from `--od-*` CSS variables with today's values as fallbacks.
// Org View's charts set smaller ones (see ZoomedTreeStyled in
// orgDesign.styles.js); the PDF capture and the PAIC preview set none, so
// they keep these defaults.
export const BoxStyled = styled.div`
  box-sizing: border-box;
  position: relative;
  border: 1px solid ${(p) => p.theme?.colors?.border ?? "#D8DAE5"};
  border-left: 4px solid ${(p) => p.theme?.colors?.[p.$accentToken] ?? p.theme?.colors?.tertiary ?? "#8790A4"};
  border-radius: 8px;
  background: #fff;
  padding: var(--od-box-padding, 8px 10px);
  margin: 4px 0;
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: var(--od-box-gap, 4px);
  width: var(--od-box-width, 200px);
  text-align: center;
`;

// A small circular chip, not a full SbButton — a bordered/backgrounded
// SbButton's own chrome is too heavy for something this small. Still a real
// <button> for keyboard/focus semantics. Absolutely positioned so it never
// contributes to BoxStyled's layout width — a collapse control here must
// not be able to shift connector centering the way an inline control could.
export const CollapseToggleStyled = styled.button`
  position: absolute;
  top: -8px;
  right: -8px;
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${(p) => p.theme?.colors?.border ?? "#D8DAE5"};
  background: #fff;
  border-radius: 50%;
  padding: 0;
  cursor: pointer;
  font-size: 11px;
  line-height: 1;
  color: ${(p) => p.theme?.colors?.textSecondary ?? "#8790A4"};
  transition: color 0.15s ease, border-color 0.15s ease;

  &:hover {
    color: ${(p) => p.theme?.colors?.primary ?? "#008080"};
    border-color: ${(p) => p.theme?.colors?.primary ?? "#008080"};
  }
`;

// Rotates the caret between collapsed (pointing right) and expanded
// (pointing down) — the standard tree-toggle animation (VSCode/GitHub/
// Notion's dropdown-style expand arrow) — via a styled prop, not an inline
// style attribute.
export const CollapseCaretStyled = styled.span`
  display: flex;
  transition: transform 0.15s ease;
  transform: rotate(${(p) => (p.$collapsed ? 0 : 90)}deg);
`;

export const CollapsedSummaryStyled = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  flex-wrap: wrap;
  font-size: var(--od-summary-size, 11.5px);
  color: ${(p) => p.theme?.colors?.textSecondary ?? "#5A6A7A"};
`;

export const TitleRowStyled = styled.div`
  font-size: var(--od-title-size, 13.5px);
  font-weight: 700;
  line-height: 1.3;
  display: flex;
  align-items: center;
  gap: 4px;
  justify-content: center;
`;

export const StatsRowStyled = styled.div`
  display: flex;
  gap: 6px;
  font-size: var(--od-stats-size, 12px);
  color: ${(p) => p.theme?.colors?.textSecondary ?? "#5A6A7A"};
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
`;

export const EditRowStyled = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 2px;
`;

export const PopoverContentStyled = styled.div`
  width: 220px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  font-size: 12px;

  .section-label { font-weight: 600; margin-bottom: 4px; }
`;

export const FullWidthSelectStyled = styled(SbSelect)`
  width: 100%;
`;

export const AddChildTitleInputStyled = styled(SbInput)`
  margin-bottom: 6px;
`;

export const AddChildRowStyled = styled.div`
  display: flex;
  gap: 6px;
  margin-bottom: 6px;
`;

export const RenameInputStyled = styled(SbInput)`
  width: 130px;
`;
